import { type RefObject, useEffect, useRef } from 'react';

type FocusEventName = 'focusin' | 'focusout';
type MouseEventName =
  | 'click'
  | 'mousedown'
  | 'mouseup'
  | 'pointerdown'
  | 'pointerup';
type TouchEventName = 'touchstart' | 'touchend';

export type ClickAwayEvent = MouseEvent | TouchEvent | FocusEvent;

export interface UseClickAwayOptions {
  /** Toggle the listeners on/off without unmounting the consumer. */
  enabled?: boolean;
  /** Mouse/pointer event to listen for, or `false` to skip. Default: `pointerdown`. */
  mouseEvent?: MouseEventName | false;
  /** Touch event to listen for, or `false` to skip. Default: `touchend`. */
  touchEvent?: TouchEventName | false;
  /** Focus event to listen for, or `false` to skip. Default: `false` (off). */
  focusEvent?: FocusEventName | false;
  /** Extra DOM events to bind to (e.g. `'keydown'` to dismiss on Escape elsewhere). */
  extraEvents?: Array<keyof DocumentEventMap>;
  /**
   * CSS selector(s) for descendants rendered outside the ref tree (typically
   * via React portals — submenus, popovers, modals spawned by menu items).
   * If the event target matches `.closest(selector)`, the handler is skipped.
   */
  ignoreSelector?: string | string[];
}

type AnyRef = RefObject<HTMLElement | null>;

/**
 * Fires `onClickAway` when an event occurs outside all of the provided refs.
 *
 *   const ref = useRef<HTMLDivElement>(null);
 *   useClickAway(ref, () => setOpen(false));
 *
 *   // Multiple refs (e.g. trigger + portaled menu content):
 *   useClickAway([triggerRef, contentRef], handleClose);
 *
 * Notes
 * - The handler ref is kept fresh without re-registering listeners.
 * - Listeners are attached in the capture phase, so they fire before
 *   stopPropagation inside the tree can swallow them.
 * - If the event target is no longer in the document by the time the listener
 *   runs (e.g. a submenu unmounted mid-click), the handler is skipped to avoid
 *   spurious closes.
 * - If you pass an array of refs, memoize it (or define it outside the
 *   component) so the listeners aren't torn down on every render.
 */
export function useClickAway(
  ref: AnyRef | AnyRef[],
  onClickAway: (event: ClickAwayEvent) => void,
  options: UseClickAwayOptions = {}
) {
  const {
    enabled = true,
    mouseEvent = 'pointerdown',
    touchEvent = 'touchend',
    focusEvent = false,
    extraEvents,
    ignoreSelector,
  } = options;

  const ignore = Array.isArray(ignoreSelector)
    ? ignoreSelector.join(',')
    : ignoreSelector;

  const onClickAwayRef = useRef(onClickAway);
  useEffect(() => {
    onClickAwayRef.current = onClickAway;
  });

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;

      const refs = Array.isArray(ref) ? ref : [ref];
      const inside = refs.some((r) => {
        const el = r.current;
        return el ? el.contains(target) : false;
      });
      if (inside) return;

      if (ignore && target instanceof Element && target.closest(ignore)) return;

      if (!document.contains(target)) return;

      onClickAwayRef.current(event as ClickAwayEvent);
    };

    const events = [
      mouseEvent || undefined,
      touchEvent || undefined,
      focusEvent || undefined,
      ...(extraEvents ?? []),
    ].filter((e): e is keyof DocumentEventMap => Boolean(e));

    events.forEach((e) => document.addEventListener(e, listener, true));
    return () => {
      events.forEach((e) => document.removeEventListener(e, listener, true));
    };
  }, [enabled, mouseEvent, touchEvent, focusEvent, extraEvents, ignore, ref]);
}
